import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import Notification, { NotificationType } from '../../features/notifications/Notification';
import { TableInfo, MenuItem, Order, MenuCategory } from '../../types';
import { OrderStorage } from '../../services/orderStorage';
import OrderStatus from '../orders/OrderStatus';
import { useLanguage } from '../../context/LanguageContext';
import LanguageSelector from '../../features/common/LanguageSelector';
import CafeStatusBanner from '../../features/cafe/components/CafeStatusBanner';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchOrganizationData } from '../../store/organizationSlice';

interface TableOrderPublicProps {
  orgSlug?: string;
  tableId?: string;
}

const TableOrderPublic: React.FC<TableOrderPublicProps> = ({ orgSlug: propOrgSlug, tableId: propTableId }) => {
  const { orgSlug: paramOrgSlug } = useParams<{ orgSlug?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const dispatch = useAppDispatch();

  // Refs for scrolling and focusing
  const orderSectionRef = useRef<HTMLDivElement>(null);
  const customerNameRef = useRef<HTMLInputElement>(null);
  const tableSelectRef = useRef<HTMLSelectElement>(null);

  // Use prop orgSlug first, then fall back to param orgSlug
  const orgSlug = propOrgSlug || paramOrgSlug;
  
  // Extract tableId from URL path if not provided as prop
  const pathSegments = location.pathname.split('/');
  const urlTableId = pathSegments[pathSegments.length - 1];
  const tableId = propTableId || (pathSegments.length > 3 && pathSegments[3] !== undefined ? urlTableId : undefined);

  const [loadingTables, setLoadingTables] = useState(false);
  const [selectedTable, setSelectedTable] = useState(tableId || '');
  const [cart, setCart] = useState<{ [key: string]: number }>({});
  const [customerName, setCustomerName] = useState('');
  const [notes, setNotes] = useState('');
  const [notification, setNotification] = useState<{ type: NotificationType; message: string } | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [localMenuItems, setLocalMenuItems] = useState<MenuItem[]>([]);
  const [localTables, setLocalTables] = useState<TableInfo[]>([]);
  const [localCategories, setLocalCategories] = useState<MenuCategory[]>([]);
  const [collapsedCategoryById, setCollapsedCategoryById] = useState<Record<number, boolean>>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOrderStatusModal, setShowOrderStatusModal] = useState(false);
  // Get cafe status from Redux store
  const { data: orgData, loading: orgLoading, error: orgError, lastUpdated } = useAppSelector((state) => state.organization);
  const cafeIsOpen = orgData?.cafeStatus?.isOpen || false;
  const cafeIsBusy = orgData?.cafeStatus?.isBusy || false;
  const cafeStatus = orgData?.cafeStatus || { isOpen: false, isBusy: false };
  const statusMessages = orgData?.statusMessages || {
    closed: "We're currently closed. Please visit us during our business hours.",
    busy: "We're experiencing high volume. Please visit our counter for faster service.",
    open: "Welcome! You can place your order below."
  };

  // Calculate if items are selected
  const hasItemsInCart = Object.values(cart).reduce((total, quantity) => total + quantity, 0) > 0;
  
  // Check if ordering is allowed
  const canPlaceOrder = cafeIsOpen && !cafeIsBusy;

  // Function to handle floating button click
  const handleFloatingButtonClick = () => {
    if (!hasItemsInCart) return;
    // Always bring the user to the final order form
    if (orderSectionRef.current) {
      orderSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    // Focus on name field to speed up finalization on mobile
    setTimeout(() => {
      if (customerNameRef.current) {
        customerNameRef.current.focus();
      }
    }, 400);
  };

  // Load organization data via Redux on component mount and when orgSlug changes
  useEffect(() => {
    if (orgSlug) {
      dispatch(fetchOrganizationData(orgSlug));
    }
  }, [orgSlug, dispatch]);

  // Load public data (menu and tables) for QR code ordering
  useEffect(() => {
    // One-time purge based on stored creation/expiry time; reads already filter
    try { OrderStorage.clearExpiredOrders(); } catch {}
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!orgSlug) {
        return;
      }
      setLoadingTables(true);
      setDataLoaded(false);
      try {
        // Only load menu data if cafe is open (cafe status is loaded by Redux)
        if (cafeIsOpen) {
          const [menuResponse, tablesResponse, categoriesResponse] = await Promise.all([
            apiService.getPublicMenu(orgSlug),
            apiService.getPublicActiveTables(orgSlug, tableId), // Get specific table if tableId is provided
            apiService.getPublicCategories(orgSlug) // Use public categories endpoint
          ]);
          
          if (menuResponse.success && menuResponse.data?.items) {
            setLocalMenuItems(menuResponse.data.items);
          }
          if (tablesResponse.success) {
            if (tableId && 'table' in tablesResponse.data) {
              // Single table response
              setLocalTables([tablesResponse.data.table]);
              setSelectedTable(tableId);
            } else if ('tables' in tablesResponse.data) {
              // Multiple tables response
              setLocalTables(tablesResponse.data.tables);
            }
          }
          if (categoriesResponse.success && categoriesResponse.data?.categories) {
            setLocalCategories(categoriesResponse.data.categories);
          }
        }
        setDataLoaded(true);
      } catch (error) {
        setNotification({ type: 'error', message: t('error.load.failed') });
      } finally {
        setLoadingTables(false);
      }
    };
    loadData();
  }, [orgSlug, tableId, cafeIsOpen]);

  // Initialize categories as collapsed by default once categories are loaded
  useEffect(() => {
    if (!localCategories || localCategories.length === 0) return;
    setCollapsedCategoryById(previous => {
      const nextState: Record<number, boolean> = { ...previous };
      localCategories.forEach(category => {
        if (!(category.id in nextState)) {
          nextState[category.id] = true;
        }
      });
      return nextState;
    });
  }, [localCategories]);

  useEffect(() => {
    if (tableId) {
      setSelectedTable(tableId);
    }
  }, [tableId]);

  // Auto-open order status modal on refresh if there are existing orders for this table
  useEffect(() => {
    if (!orgSlug || !selectedTable) return;
    try {
      const existing = OrderStorage.getOrdersForTable(orgSlug, selectedTable);
      if (existing.length > 0) {
        setShowOrderStatusModal(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch {}
  }, [orgSlug, selectedTable]);

  // Open order status modal whenever a new order is saved for this table
  useEffect(() => {
    const handler = (event: any) => {
      try {
        const { action, order } = event.detail || {};
        if (action === 'add' && order?.orgUrl === orgSlug && order?.tableId === selectedTable) {
          setShowOrderStatusModal(true);
        }
      } catch {}
    };
    window.addEventListener('orderStorageChanged', handler as EventListener);
    return () => window.removeEventListener('orderStorageChanged', handler as EventListener);
  }, [orgSlug, selectedTable]);

  const displayMenuItems = localMenuItems;
  const displayTables = localTables;
  const table = displayTables.find(t => t.id.toString() === selectedTable.toString());

  if (loadingTables || orgLoading) {
    return (
      <div className="main-content">
        <LanguageSelector />
        <div className="hero" style={{ background: 'linear-gradient(135deg, rgba(48, 209, 88, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)' }}>
          <div className="loading-container">
            <div className="loading-spinner-container">
              <div className="loading-spinner-element"></div>
              <h2>{t('loading.menu.tables')}</h2>
              <p>{t('loading.menu.tables.description')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!orgSlug) {
    return (
      <div className="main-content">
        <LanguageSelector />
        <div className="hero" style={{ background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)' }}>
          <h1 style={{ color: '#ff3b30' }}>{t('error.invalid.url')}</h1>
          <p>{t('error.invalid.url.description')}</p>
          <button onClick={() => navigate('/')} className="btn btn-secondary">{t('nav.back.home')}</button>
        </div>
      </div>
    );
  }

  // Show error state if organization data failed to load
  if (orgError && !orgLoading) {
    return (
      <div className="main-content">
        <LanguageSelector />
        <div className="hero" style={{ background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)' }}>
          <h1 style={{ color: '#ff3b30' }}>{t('error.organization.load.failed')}</h1>
          <p>{orgError}</p>
          <button onClick={() => navigate('/')} className="btn btn-secondary">{t('nav.back.home')}</button>
        </div>
      </div>
    );
  }

  // Only validate table availability if cafe is open and not busy
  if (cafeIsOpen && !cafeIsBusy && selectedTable && table && !table.isActive) {
    return (
      <div className="main-content">
        <LanguageSelector />
        <div className="hero" style={{ background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)' }}>
          <h1 style={{ color: '#ff3b30' }}>{t('error.table.unavailable')}</h1>
          <p>{t('error.table.unavailable.description')}</p>
          <button onClick={() => navigate('/')} className="btn btn-secondary">{t('nav.back.menu')}</button>
        </div>
      </div>
    );
  }

  // Only validate table if cafe is open and not busy
  if (cafeIsOpen && !cafeIsBusy && selectedTable && !table) {
    return (
      <div className="main-content">
        <LanguageSelector />
        <div className="hero" style={{ background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)' }}>
          <h1 style={{ color: '#ff3b30' }}>{t('error.table.not.found')}</h1>
          <p>{t('error.table.not.found.description')}</p>
          <button onClick={() => navigate('/')} className="btn btn-secondary">{t('nav.back.menu')}</button>
        </div>
      </div>
    );
  }

  if (!dataLoaded) {
    return (
      <div className="main-content">
        <LanguageSelector />
        <div className="hero" style={{ background: 'linear-gradient(135deg, rgba(48, 209, 88, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)' }}>
          <h1>{t('loading.menu')}</h1>
          <p>{t('loading.menu.description')}</p>
        </div>
      </div>
    );
  }

  const addToCart = (itemId: number) => {
    setCart(prev => ({ ...prev, [itemId.toString()]: (prev[itemId.toString()] || 0) + 1 }));
  };

  const removeFromCart = (itemId: number) => {
    setCart(prev => {
      const newCart = { ...prev };
      if (newCart[itemId.toString()] > 1) {
        newCart[itemId.toString()]--;
      } else {
        delete newCart[itemId.toString()];
      }
      return newCart;
    });
  };

  const removeItemCompletely = (itemId: number) => {
    setCart(previous => {
      const next = { ...previous };
      delete next[itemId.toString()];
      return next;
    });
  };

  const toggleCategoryCollapsed = (categoryId: number) => {
    setCollapsedCategoryById(previous => ({
      ...previous,
      [categoryId]: !previous[categoryId]
    }));
  };

  const getItemsForCategory = (categoryId: number) => {
    return displayMenuItems.filter(item => item.categoryId === categoryId);
  };

  const getTotalPrice = () => {
    return Object.entries(cart).reduce((total, [itemId, quantity]) => {
      const item = displayMenuItems.find(i => i.id === parseInt(itemId));
      return total + (item ? item.price * quantity : 0);
    }, 0);
  };

  const placeOrder = async () => {
    if (isSubmitting) return;
    if (!canPlaceOrder) {
      setNotification({ type: 'error', message: t('notification.cafe.not.accepting') });
      return;
    }
    if (!customerName.trim()) {
      setNotification({ type: 'error', message: t('notification.enter.name') });
      return;
    }
    if (Object.keys(cart).length === 0) {
      setNotification({ type: 'error', message: t('notification.add.items') });
      return;
    }
    if (!selectedTable) {
      setNotification({ type: 'error', message: t('notification.select.table') });
      return;
    }

    // Check cafe status before placing order
    if (orgSlug) {
      try {
        const statusResponse = await apiService.getPublicOrganization(orgSlug);
        if (statusResponse.success && statusResponse.data?.organization) {
          const currentStatus = statusResponse.data.organization.cafeStatus;
          if (!currentStatus?.isOpen || currentStatus?.isBusy) {
            setNotification({
              type: 'error',
              message: t('notification.cafe.not.accepting')
            });
            return;
          }
        } else {
          setNotification({ 
            type: 'error', 
            message: t('error.organization.load.failed') 
          });
          return;
        }
      } catch (error) {
        console.error('Error checking cafe status:', error);
        setNotification({ 
          type: 'error', 
          message: t('error.organization.load.failed') 
        });
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const orderItems = Object.entries(cart).map(([itemId, quantity]) => {
        const item = displayMenuItems.find(i => i.id === parseInt(itemId));
        if (!item) throw new Error(`Menu item ${itemId} not found`);
        
        return {
          menuItem: item,
          quantity,
          specialRequests: ''
        };
      });
      
      const order: Omit<Order, 'id' | 'timestamp' | 'shopId'> = {
        customerName: customerName.trim(),
        items: orderItems,
        total: getTotalPrice(),
        status: 'pending',
        serviceType: 'table',
        tableName: table?.name || 'Unknown',
        specialRequests: '',
        notes: notes.trim(),
        source: 'qr_code'
      };
      
      // Use the public order creation endpoint
      const response = await apiService.createPublicOrder(orgSlug!, selectedTable, order);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create order');
      }
      
      const createdOrder = response.data!;

      // Save order to localStorage
      const orderData = {
        orderId: createdOrder.orderId,
        orderNumber: createdOrder.orderNumber,
        orgUrl: createdOrder.orgUrl,
        tableId: createdOrder.tableId,
        estimatedTime: createdOrder.estimatedTime,
        customerName: customerName.trim(),
        total: getTotalPrice()
      };
      
      OrderStorage.addOrder(orderData);

      setCart({});
      setCustomerName('');
      setNotes('');
      setShowConfirmModal(false);
      setShowOrderStatusModal(true);
      setNotification({ type: 'success', message: `${t('notification.order.success')}${createdOrder.orderNumber}. ${t('notification.order.success.description')}` });
      setTimeout(() => setNotification(null), 5000);
    } catch (error) {
      console.error('Failed to place order:', error);
      setNotification({ type: 'error', message: t('error.order.failed') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openConfirmModal = () => {
    if (!customerName.trim()) {
      setNotification({ type: 'error', message: t('notification.enter.name') });
      if (orderSectionRef.current) {
        orderSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => customerNameRef.current?.focus(), 400);
      return;
    }
    if (!selectedTable) {
      setNotification({ type: 'error', message: t('notification.select.table') });
      if (orderSectionRef.current) {
        orderSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => tableSelectRef.current?.focus(), 400);
      return;
    }
    if (!hasItemsInCart) {
      setNotification({ type: 'error', message: t('notification.add.items') });
      return;
    }
    setShowConfirmModal(true);
  };

  return (
    <div className="main-content">
      <LanguageSelector />
      {notification && (
        <div className="notification">
          <Notification type={notification.type} message={notification.message} onClose={() => setNotification(null)} autoClose={true} duration={4000} />
        </div>
      )}
      {/* {orgSlug && (!cafeIsOpen && cafeIsBusy) && (
        //todo make it inline style for the cafe status banner
        <CafeStatusBanner 
          orgSlug={orgSlug}
          cafeStatus={cafeStatus}
          statusMessages={statusMessages}
          loading={orgLoading}
        />
      )} */}
      
      {/* Full-screen overlay for busy cafe */}
      {!orgLoading && cafeIsOpen && cafeIsBusy && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 193, 7, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px'
          }}>
            <LanguageSelector />
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '500px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '20px'
            }}>⏳</div>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: '#d97706',
              marginBottom: '16px'
            }}>{t('cafe.status.busy.title')}</h1>
            <p style={{
              fontSize: '1.1rem',
              color: '#6b7280',
              lineHeight: '1.6',
              marginBottom: '24px'
            }}>
              {statusMessages.busy}
            </p>
            <div style={{
              fontSize: '0.9rem',
              color: '#9ca3af',
              fontStyle: 'italic'
            }}>
              {t('cafe.status.busy.subtitle')}
            </div>
          </div>
        </div>
      )}
      
      {/* Full-screen overlay for closed cafe */}
      {!orgLoading && !cafeIsOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(220, 38, 38, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px'
          }}>
            <LanguageSelector />
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '500px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '20px'
            }}>🚫</div>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: '#dc2626',
              marginBottom: '16px'
            }}>{t('cafe.status.closed.title')}</h1>
            <p style={{
              fontSize: '1.1rem',
              color: '#6b7280',
              lineHeight: '1.6',
              marginBottom: '24px'
            }}>
              {statusMessages.closed}
            </p>
            <div style={{
              fontSize: '0.9rem',
              color: '#9ca3af',
              fontStyle: 'italic'
            }}>
              {t('cafe.status.closed.subtitle')}
            </div>
          </div>
        </div>
      )}
      
      {cafeIsOpen && !cafeIsBusy && (
      <div className="hero" style={{ background: 'linear-gradient(135deg, rgba(48, 209, 88, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)' }}>
        <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1d1d1f', margin: '0 0 0.5rem 0' }}>
            {orgSlug === 'beanbuzz-demo' ? 'BeanBuzz Demo Cafe' : orgSlug?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </h2>
        </div>
        <h1>{t('order.from.table')}</h1>
        <p>{t('order.description')}</p>
        {table && (
          <div style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', backgroundColor: 'rgba(48, 209, 88, 0.1)', borderRadius: '12px', display: 'inline-block' }}>
            <strong>{t('table.label')}: {table.name}</strong>
          </div>
        )}
      </div>
      )}

        {/* Show existing orders status */}
        {orgSlug && selectedTable && (
          <OrderStatus 
            key={`${orgSlug}-${selectedTable}`}
            orgUrl={orgSlug} 
            tableId={selectedTable} 
          />
        )}
      

      {cafeIsOpen && !cafeIsBusy && (
        <div className="table-order-grid">
          <div className="menu-section">
            {localCategories
              .filter(category => category.isActive)
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map(category => {
                const itemsInCategory = getItemsForCategory(category.id);
                const isCollapsed = !!collapsedCategoryById[category.id];
                return (
                  <div key={category.id} className="menu-category" style={{ borderBottom: '1px solid #eee', paddingBottom: '12px', marginBottom: '12px' }}>
                    <button
                      onClick={() => toggleCategoryCollapsed(category.id)}
                      aria-expanded={!isCollapsed}
                      aria-controls={`category-panel-${category.id}`}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        background: 'transparent',
                        border: 'none',
                        padding: '12px 0',
                        cursor: 'pointer',
                        minHeight: '44px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          style={{
                            fontSize: '20px',
                            lineHeight: 1,
                            display: 'inline-block',
                            transition: 'transform 200ms ease',
                            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)'
                          }}
                        >
                          ▸
                        </span>
                        <h3 style={{ margin: 0 }}>{category.name}</h3>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '0.9rem' }}>
                        <span style={{ backgroundColor: '#f2f2f7', borderRadius: '999px', padding: '2px 8px' }}>{itemsInCategory.length} {t('order.items')}</span>
                      </div>
                    </button>
                    <div id={`category-panel-${category.id}`}>
                      {/* Collapsed summary (animated show/hide) */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateRows: isCollapsed ? '1fr' : '0fr',
                          transition: 'grid-template-rows 250ms ease',
                          paddingLeft: '28px'
                        }}
                      >
                        <div
                          style={{
                            overflow: 'hidden',
                            color: '#6e6e73',
                            fontSize: '0.95rem',
                            opacity: isCollapsed ? 1 : 0,
                            transition: 'opacity 250ms ease'
                          }}
                        >
                          {category.description && (
                            <div style={{ marginBottom: '6px' }}>{category.description}</div>
                          )}
                          <div>{itemsInCategory.length} {t('order.items')}</div>
                        </div>
                      </div>

                      {/* Expanded items (animated show/hide) */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateRows: isCollapsed ? '0fr' : '1fr',
                          transition: 'grid-template-rows 300ms ease'
                        }}
                      >
                        <div style={{ overflow: 'hidden' }}>
                          {itemsInCategory.map(item => (
                            <div key={item.id} className="menu-item">
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                                {item.imageUrl && (
                                  <div style={{ width: '80px', height: '80px', borderRadius: '12px', overflow: 'hidden', flexShrink: 0 }}>
                                    <img
                                      src={item.imageUrl}
                                      alt={item.name}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  </div>
                                )}
                                <div className="menu-item-info">
                                  <div className="menu-item-name">{item.name}</div>
                                  <div className="menu-item-description">{item.description}</div>
                                  <div className="menu-item-price">${item.price.toFixed(2)}</div>
                                </div>
                              </div>
                              <div className="menu-item-actions">
                                <div className="quantity-controls">
                                  <button className="quantity-btn" onClick={() => removeFromCart(item.id)} disabled={!cart[item.id.toString()]}>−</button>
                                  <span style={{ minWidth: '32px', textAlign: 'center', fontWeight: '600', fontSize: '0.875rem', color: '#1d1d1f' }}>{cart[item.id.toString()] || 0}</span>
                                  <button className="quantity-btn" onClick={() => addToCart(item.id)}>+</button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {/* Bottom collapse control with divider */}
                          <div style={{ position: 'relative', margin: '12px 0 4px 0', height: '28px' }}>
                            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', backgroundColor: '#eee', transform: 'translateY(-50%)' }} />
                            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                              <button
                                onClick={() => toggleCategoryCollapsed(category.id)}
                                style={{
                                  background: '#ffffff',
                                  border: '1px solid #e5e5ea',
                                  color: '#1d1d1f',
                                  padding: '6px 10px',
                                  borderRadius: '999px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  lineHeight: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                                }}
                              >
                                <span style={{ display: 'inline-block', transform: 'translateY(-1px)' }}>▴</span>
                                {t('category.collapse')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          <div className="order-section" ref={orderSectionRef}>
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  {t('order.your.order')}
                  {Object.values(cart).reduce((total, quantity) => total + quantity, 0) > 0 && (
                    <span style={{ backgroundColor: '#30d158', color: 'white', fontSize: '0.8rem', padding: '0.25rem 0.5rem', borderRadius: '12px', marginLeft: '0.5rem' }}>{Object.values(cart).reduce((total, quantity) => total + quantity, 0)} {t('order.items')}</span>
                  )}
                </h3>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="customerName">{t('form.name.label')}</label>
                <input 
                  id="customerName" 
                  ref={customerNameRef}
                  type="text" 
                  className="form-input" 
                  value={customerName} 
                  onChange={e => setCustomerName(e.target.value)} 
                  placeholder={t('form.name.placeholder')} 
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="tableSelect">{t('form.table.label')}</label>
                <select id="tableSelect" ref={tableSelectRef} className="form-select" value={selectedTable} onChange={e => setSelectedTable(e.target.value)} disabled={!!tableId}>
                  <option value="">{t('form.table.select.placeholder')}</option>
                  {displayTables.length === 0 ? (
                    <option value="" disabled>{t('form.table.no.tables')}</option>
                  ) : (
                    displayTables.filter(table => table.isActive).map(table => (
                      <option key={table.id} value={table.id}>{table.name}</option>
                    ))
                  )}
                </select>
                {displayTables.length === 0 && (
                  <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>{t('form.table.no.tables.description')}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="orderNotes">{t('form.notes.label')}</label>
                <textarea id="orderNotes" className="form-textarea" value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('form.notes.placeholder')} rows={3} />
              </div>
              <div className="order-summary">
                <h4 style={{ marginBottom: '16px', fontSize: '1.125rem', fontWeight: '600', color: '#1d1d1f' }}>{t('order.summary')}</h4>
                {Object.entries(cart).filter(([, quantity]) => quantity > 0).map(([itemId, quantity]) => {
                  const item = displayMenuItems.find(item => item.id === parseInt(itemId))!;
                  return (
                    <div key={itemId} className="order-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          aria-label="Remove item"
                          onClick={() => removeItemCompletely(item.id)}
                          style={{
                            border: 'none',
                            background: '#fff1f2',
                            color: '#d90429',
                            borderRadius: '8px',
                            padding: '4px 8px',
                            cursor: 'pointer'
                          }}
                        >✕</button>
                        <span>{item.name} × {quantity}</span>
                      </div>
                      <span style={{ fontWeight: '600' }}>${(item.price * quantity).toFixed(2)}</span>
                    </div>
                  );
                })}
                <div className="order-total">{t('order.total')}: ${getTotalPrice().toFixed(2)}</div>
              </div>
              <button className="btn btn-primary" onClick={openConfirmModal} style={{ width: '100%', marginTop: '16px' }}>{t('order.place')}</button>
            </div>
          </div>
        </div>
        )}
        {cafeIsOpen && !cafeIsBusy && (
        <button 
          className="mobile-place-order-btn" 
          onClick={handleFloatingButtonClick}
          disabled={!hasItemsInCart || !canPlaceOrder}
          style={{
            opacity: (hasItemsInCart && canPlaceOrder) ? 1 : 0.5,
            cursor: (hasItemsInCart && canPlaceOrder) ? 'pointer' : 'not-allowed',
            backgroundColor: (hasItemsInCart && canPlaceOrder) ? '#30d158' : '#ccc',
            transform: (hasItemsInCart && canPlaceOrder) ? 'translateY(0)' : 'none',
            boxShadow: (hasItemsInCart && canPlaceOrder) ? '0 4px 16px rgba(48, 209, 88, 0.3)' : 'none'
          }}
        >
          {!hasItemsInCart 
            ? t('order.add.items') 
            : `${t('order.ready.finalize')} - $${getTotalPrice().toFixed(2)}`
          }
        </button>
      )}
      {showConfirmModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-order-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            zIndex: 9999
          }}
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '520px',
              background: 'white',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 id="confirm-order-title" style={{ margin: 0 }}>{t('order.summary')}</h3>
              <button
                aria-label="Close"
                onClick={() => setShowConfirmModal(false)}
                style={{ border: 'none', background: 'transparent', fontSize: '20px', cursor: 'pointer' }}
              >✕</button>
            </div>
            <div style={{ padding: '16px 20px', maxHeight: '60vh', overflowY: 'auto' }}>
              {Object.entries(cart).filter(([, quantity]) => quantity > 0).map(([itemId, quantity]) => {
                const item = displayMenuItems.find(item => item.id === parseInt(itemId))!;
                return (
                  <div key={itemId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '8px 0', borderBottom: '1px dashed #eee' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        aria-label="Remove item"
                        onClick={() => removeItemCompletely(item.id)}
                        style={{
                          border: 'none',
                          background: '#fff1f2',
                          color: '#d90429',
                          borderRadius: '8px',
                          padding: '4px 8px',
                          cursor: 'pointer'
                        }}
                      >✕</button>
                      <span>{item.name} × {quantity}</span>
                    </div>
                    <span style={{ fontWeight: 600 }}>${(item.price * quantity).toFixed(2)}</span>
                  </div>
                );
              })}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px', fontSize: '1.05rem' }}>
                <strong>{t('order.total')}</strong>
                <strong>${getTotalPrice().toFixed(2)}</strong>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #eee', display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  flex: 1,
                  background: '#f2f2f7',
                  border: 'none',
                  color: '#1d1d1f',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >{t('nav.back.menu') || 'Cancel'}</button>
              <button
                onClick={placeOrder}
                disabled={isSubmitting || Object.keys(cart).length === 0 || !canPlaceOrder}
                style={{
                  flex: 1,
                  background: canPlaceOrder ? '#30d158' : '#9ca3af',
                  border: 'none',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  cursor: (isSubmitting || !canPlaceOrder) ? 'not-allowed' : 'pointer',
                  opacity: (isSubmitting || !canPlaceOrder) ? 0.7 : 1,
                  fontWeight: 700
                }}
              >{isSubmitting ? t('loading.processing') : !canPlaceOrder ? t('button.cafe.not.accepting') : t('order.place')}</button>
            </div>
          </div>
        </div>
      )}

      {showOrderStatusModal && orgSlug && selectedTable && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="order-status-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 10000
          }}
        >
          <div style={{
            padding: '14px 16px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <h3 id="order-status-title" style={{ margin: 0 }}>{t('order.your.order')}</h3>
            <button
              aria-label="Close"
              onClick={() => setShowOrderStatusModal(false)}
              style={{ border: 'none', background: 'transparent', fontSize: '20px', cursor: 'pointer' }}
            >✕</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 16px 12px' }}>
            <OrderStatus orgUrl={orgSlug} tableId={selectedTable} />
          </div>
          <div style={{ padding: '12px 16px', borderTop: '1px solid #eee' }}>
            <button
              onClick={() => setShowOrderStatusModal(false)}
              style={{
                width: '100%',
                background: '#1d1d1f',
                color: '#ffffff',
                border: 'none',
                padding: '12px 16px',
                borderRadius: '12px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >{t('nav.back.menu')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableOrderPublic; 